import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import {
  Bot, Plus, Trash2, RefreshCw, ExternalLink, Search,
  Check, X, Link2, ChevronDown, History, AlertCircle, Pencil, ArrowLeft
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import config from '../config';
import { nomDePays, nomDeLangue, trierParNom } from '../utils/libellesInternationaux';

const API_URL = config.API_URL;

/**
 * Pays et langues servis quand les listes complètes n'arrivent pas — mêmes replis que le
 * serveur, pour le cas où c'est la requête elle-même qui échoue. Les codes sont ceux de
 * DataForSEO : un prompt créé depuis ces replis reste valide.
 */
const PAYS_DE_REPLI = [
  { code: 2250, name: 'France', countryIsoCode: 'FR' },
  { code: 2056, name: 'Belgium', countryIsoCode: 'BE' },
  { code: 2756, name: 'Switzerland', countryIsoCode: 'CH' },
  { code: 2124, name: 'Canada', countryIsoCode: 'CA' },
  { code: 2840, name: 'United States', countryIsoCode: 'US' },
  { code: 2826, name: 'United Kingdom', countryIsoCode: 'GB' },
  { code: 2276, name: 'Germany', countryIsoCode: 'DE' },
  { code: 2724, name: 'Spain', countryIsoCode: 'ES' },
  { code: 2380, name: 'Italy', countryIsoCode: 'IT' },
  { code: 2620, name: 'Portugal', countryIsoCode: 'PT' },
  { code: 2528, name: 'Netherlands', countryIsoCode: 'NL' },
  { code: 2442, name: 'Luxembourg', countryIsoCode: 'LU' }
];

const LANGUES_DE_REPLI = [
  { code: 'fr', name: 'French' },
  { code: 'en', name: 'English' },
  { code: 'de', name: 'German' },
  { code: 'es', name: 'Spanish' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'nl', name: 'Dutch' }
];

// LLM configurations
const LLM_CONFIG = {
  CHATGPT: { name: 'ChatGPT', color: '#10a37f', url: 'https://chat.openai.com/' },
  CLAUDE: { name: 'Claude', color: '#d97706', url: 'https://claude.ai/new' },
  GEMINI: { name: 'Gemini', color: '#4285f4', url: 'https://gemini.google.com/app' },
  PERPLEXITY: { name: 'Perplexity', color: '#7c3aed', url: 'https://www.perplexity.ai/' }
};

// Language names mapping
const LANG_NAMES = {
  'fr': 'français', 'en': 'English', 'de': 'Deutsch', 'es': 'español',
  'it': 'italiano', 'pt': 'português', 'nl': 'Nederlands', 'pl': 'polski',
  'ru': 'русский', 'ja': '日本語', 'zh': '中文', 'ko': '한국어', 'ar': 'العربية'
};

// Styles
const styles = {
  // Pleine largeur, comme SERP Tracking: c'est .tools-view qui gère les marges
  container: {
    width: '100%',
    padding: 0
  },
  card: {
    background: '#fff',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: '24px'
  },
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s'
  },
  buttonPrimary: {
    background: '#3b82f6',
    color: '#fff'
  },
  buttonSuccess: {
    background: '#10b981',
    color: '#fff'
  },
  buttonSecondary: {
    background: '#f3f4f6',
    color: '#374151'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  modal: {
    background: '#fff',
    borderRadius: '16px',
    padding: '24px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto'
  }
};

// InfoTooltip component
const InfoTooltip = ({ children, text, position = 'top' }) => {
  const [show, setShow] = useState(false);

  const getTooltipStyle = () => {
    const base = {
      position: 'absolute',
      padding: '6px 10px',
      backgroundColor: '#1f2937',
      color: '#fff',
      fontSize: '12px',
      borderRadius: '6px',
      whiteSpace: 'nowrap',
      zIndex: 1000,
      pointerEvents: 'none',
      textTransform: 'none'
    };
    switch (position) {
      case 'bottom':
        return { ...base, top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '6px' };
      case 'left':
        return { ...base, bottom: '100%', right: '0', marginBottom: '6px' };
      default:
        return { ...base, bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '6px' };
    }
  };

  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && <span style={getTooltipStyle()}>{text}</span>}
    </span>
  );
};

const AiVisibilityTool = ({ token, selectedClientId }) => {
  // État - Clients
  const [clients, setClients] = useState([]);

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Modals
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showAddPromptsModal, setShowAddPromptsModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [promptHistory, setPromptHistory] = useState([]);
  const [locations, setLocations] = useState([]);
  const [languages, setLanguages] = useState([]);
  // Renseigné quand seuls les pays de repli ont pu être servis : la modale le dit, plutôt que
  // d'afficher une liste courte sans explication.
  const [listesDegrade, setListesDegrade] = useState(null);

  // Form states
  const [newProject, setNewProject] = useState({ name: '', domain: '', clientId: '' });
  const [projetAEditer, setProjetAEditer] = useState(null);
  const [newPrompts, setNewPrompts] = useState({
    prompts: '',
    llmModels: ['CHATGPT', 'CLAUDE', 'GEMINI', 'PERPLEXITY'],
    frequency: 'WEEKLY',
    locationCode: 2250,
    locationName: 'France',
    languageCode: 'fr'
  });

  // Checking state
  const [checkingPrompt, setCheckingPrompt] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  // Load projects with retry
  const loadProjects = useCallback(async (retryCount = 0) => {
    try {
      setError('');
      const params = selectedClientId ? `?client=${selectedClientId}` : '';
      const response = await axios.get(`${API_URL}/api/tools/ai-visibility/projects${params}`, { headers });
      const projectsList = response.data.data || [];
      setProjects(projectsList);
      // Ne pas rester sur le détail d'un projet qui ne fait plus partie du client sélectionné
      setSelectedProject(prev => (prev && projectsList.some(p => p.id === prev.id) ? prev : null));
    } catch (err) {
      console.error('[AI Visibility] Error loading projects:', err);
      // Retry up to 3 times with 1s delay (backend might still be starting)
      if (retryCount < 3 && err.message === 'Network Error') {
        console.log(`[AI Visibility] Retrying in 1s... (attempt ${retryCount + 1}/3)`);
        setTimeout(() => loadProjects(retryCount + 1), 1000);
      } else {
        setError('Erreur chargement projets');
      }
    }
  }, [selectedClientId, token]);

  // Load project overview
  const loadOverview = useCallback(async (projectId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/tools/ai-visibility/projects/${projectId}/overview`, { headers });
      setOverview(response.data.data);
    } catch (err) {
      setError('Erreur chargement overview');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Charger les clients (pour associer les projets)
  const loadClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client-management/clients`, { headers });
      setClients(response.data || []);
    } catch (err) {
      console.error('Erreur chargement clients:', err);
    }
  }, [token]);

  // Load prompt history
  const loadPromptHistory = async (promptId) => {
    try {
      const response = await axios.get(`${API_URL}/api/tools/ai-visibility/prompts/${promptId}/history?days=30`, { headers });
      setPromptHistory(response.data.data?.history || []);
    } catch (err) {
      setError('Erreur chargement historique');
    }
  };

  // Modifier l'association client d'un projet

  useEffect(() => {
    loadProjects();
    loadClients();
  }, [loadProjects, loadClients]);

  useEffect(() => {
    if (selectedProject?.id) {
      loadOverview(selectedProject.id);
    }
  }, [selectedProject?.id, loadOverview]);

  // Load locations and languages (reuse SERP tracking API)
  //
  // `allSettled` et non `all` : les deux listes sont indépendantes, et un échec sur les langues
  // vidait aussi la liste des pays — donc un `<select>` Pays vide, impossible à ouvrir, sans
  // rien à l'écran pour l'expliquer. Chaque liste retombe sur son repli de son côté.
  useEffect(() => {
    const loadLocationsAndLanguages = async () => {
      const [loc, lang] = await Promise.allSettled([
        axios.get(`${API_URL}/api/tools/serp-tracking/locations`, { headers }),
        axios.get(`${API_URL}/api/tools/serp-tracking/languages`, { headers })
      ]);

      const avertissements = [];

      if (loc.status === 'fulfilled') {
        setLocations(loc.value.data.data || PAYS_DE_REPLI);
        if (loc.value.data.degrade) avertissements.push(loc.value.data.degrade);
      } else {
        console.error('Erreur chargement locations:', loc.reason);
        setLocations(PAYS_DE_REPLI);
        avertissements.push('Liste des pays indisponible : seuls les principaux pays sont proposés.');
      }

      if (lang.status === 'fulfilled') {
        setLanguages(lang.value.data.data || LANGUES_DE_REPLI);
        if (lang.value.data.degrade) avertissements.push(lang.value.data.degrade);
      } else {
        console.error('Erreur chargement languages:', lang.reason);
        setLanguages(LANGUES_DE_REPLI);
        avertissements.push('Liste des langues indisponible : seules les principales langues sont proposées.');
      }

      // Les deux listes partagent un seul avertissement : deux encarts côte à côte pour la même
      // panne d'API diraient deux fois la même chose.
      setListesDegrade(avertissements.length ? avertissements.join(' ') : null);
    };
    loadLocationsAndLanguages();
  }, [token]);

  // Create project
  const createProject = async () => {
    if (!newProject.name || !newProject.domain) return;
    try {
      const response = await axios.post(`${API_URL}/api/tools/ai-visibility/projects`, {
        name: newProject.name,
        domain: newProject.domain,
        clientId: newProject.clientId || null
      }, { headers });
      setShowNewProjectModal(false);
      setNewProject({ name: '', domain: '', clientId: '' });
      await loadProjects();
      setSelectedProject(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur creation projet');
    }
  };

  // Delete project
  // Modifier un projet depuis sa carte. C'est le seul endroit où se règle le rattachement
  // client après la création — le dropdown de la barre, qui passait pour un filtre alors
  // qu'il écrivait en base, a été retiré.
  const enregistrerProjet = async () => {
    if (!projetAEditer?.name?.trim() || !projetAEditer?.domain?.trim()) return;
    try {
      await axios.put(
        `${API_URL}/api/tools/ai-visibility/projects/${projetAEditer.id}`,
        {
          name: projetAEditer.name.trim(),
          domain: projetAEditer.domain.trim(),
          clientId: projetAEditer.clientId || null
        },
        { headers }
      );
      setProjetAEditer(null);
      loadProjects();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur modification projet');
    }
  };

  /**
   * Interroger tous les prompts du projet.
   *
   * Le bouton relisait la base sans rien interroger. Ici chaque prompt sollicite quatre
   * modèles et la requête attend le tout : la confirmation annonce donc le volume, et le
   * serveur plafonne pour ne pas dépasser le délai du navigateur.
   */
  const [releveEnCours, setReleveEnCours] = useState(false);

  const releverTout = async () => {
    if (!selectedProject) return;
    const nombre = overview?.prompts?.length || 0;
    if (nombre === 0) return;
    if (!window.confirm(
      `Interroger les ${nombre} prompt${nombre > 1 ? 's' : ''} de ce projet sur les quatre modèles ?`
      + '\n\nCela peut prendre une à deux minutes.'
    )) return;

    setReleveEnCours(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/tools/ai-visibility/projects/${selectedProject.id}/check`, {}, { headers }
      );
      const d = data.data || {};
      const reste = d.restants > 0 ? ` ${d.restants} prompt(s) non traités, relancez pour les suivants.` : '';
      setError(d.erreurs > 0
        ? `${d.succes}/${d.demandes} prompts interrogés — ${d.erreurs} en échec.${reste}`
        : '');
      await loadOverview(selectedProject.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du relevé');
    } finally {
      setReleveEnCours(false);
    }
  };

  const deleteProject = async (projectId) => {
    const projet = projects.find(p => p.id === projectId);
    // La confirmation nomme le projet — depuis une grille de cartes, « ce projet » ne désigne
    // rien — et décrit ce qui se passe vraiment : la suppression est définitive et emporte
    // les relevés. L'annoncer permet d'hésiter à bon escient.
    const nombre = projet?._count?.prompts || 0;
    const detail = nombre > 0
      ? ` Ses ${nombre} prompt${nombre > 1 ? 's' : ''} et leurs relevés seront supprimés.`
      : '';
    if (!window.confirm(
      `Supprimer « ${projet?.name || 'ce projet'} » ?${detail}`
      + '\n\nCette suppression est définitive : l\'historique des relevés ne sera pas récupérable.'
    )) return;

    try {
      await axios.delete(`${API_URL}/api/tools/ai-visibility/projects/${projectId}`, { headers });
      setSelectedProject(null);
      setOverview(null);
      await loadProjects();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur suppression projet');
    }
  };

  // Add prompts
  const addPrompts = async () => {
    const promptsList = newPrompts.prompts
      .split('\n')
      .map(p => p.trim())
      .filter(p => p.length > 0);

    if (promptsList.length === 0) return;

    // Récupérer le code ISO du pays depuis la location
    const selectedLocation = locations.find(l => l.code === newPrompts.locationCode);
    const countryCode = selectedLocation?.country_iso_code || 'FR';

    try {
      const payload = {
        prompts: promptsList.map(prompt => ({
          prompt,
          llmModels: newPrompts.llmModels,
          frequency: newPrompts.frequency,
          languageCode: newPrompts.languageCode,
          countryCode: countryCode
        }))
      };
      await axios.post(`${API_URL}/api/tools/ai-visibility/projects/${selectedProject.id}/prompts`, payload, { headers });
      setShowAddPromptsModal(false);
      setNewPrompts({ ...newPrompts, prompts: '' });
      loadOverview(selectedProject.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur ajout prompts');
    }
  };

  // Delete prompt
  const deletePrompt = async (promptId) => {
    try {
      await axios.delete(`${API_URL}/api/tools/ai-visibility/prompts/${promptId}`, { headers });
      loadOverview(selectedProject.id);
    } catch (err) {
      setError('Erreur suppression prompt');
    }
  };

  // Check prompt
  const checkPrompt = async (promptId) => {
    setCheckingPrompt(promptId);
    try {
      await axios.post(`${API_URL}/api/tools/ai-visibility/prompts/${promptId}/check`, {}, { headers });
      loadOverview(selectedProject.id);
    } catch (err) {
      setError('Erreur vérification prompt');
    } finally {
      setCheckingPrompt(null);
    }
  };

  // State for copy feedback
  const [copiedPrompt, setCopiedPrompt] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  // Open LLM with prompt
  const handleOpenLlm = (model, promptText, languageCode, countryCode) => {
    const llmConfig = LLM_CONFIG[model];
    const langName = LANG_NAMES[languageCode] || languageCode || 'français';
    // Traduit aussi : la consigne envoyée au LLM est rédigée en français, « contexte
    // géographique : Belgium » y détonnait.
    const pays = locations.find(l => l.countryIsoCode === countryCode);
    const locationName = nomDePays(countryCode, pays?.name || countryCode || '');

    // Build contextual prompt
    let contextualPrompt = promptText;
    if (langName || locationName) {
      contextualPrompt += `\n\n(Réponds en ${langName}`;
      if (locationName) {
        contextualPrompt += `, contexte géographique : ${locationName}`;
      }
      contextualPrompt += ')';
    }

    // Copy to clipboard using execCommand fallback for broader support
    const textArea = document.createElement('textarea');
    textArea.value = contextualPrompt;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);

    // Show toast for LLMs that don't support URL params
    if (model !== 'PERPLEXITY') {
      setToastMessage(`Prompt copié ! Collez-le dans ${llmConfig.name} (Ctrl+V / Cmd+V)`);
      setTimeout(() => setToastMessage(null), 4000);
    }

    // Reset icon feedback after 2s
    setTimeout(() => setCopiedPrompt(null), 2000);

    // Open LLM - Perplexity supports URL params
    if (model === 'PERPLEXITY') {
      window.open(`https://www.perplexity.ai/search?q=${encodeURIComponent(contextualPrompt)}`, '_blank');
    } else {
      window.open(llmConfig.url, '_blank');
    }
  };

  // Render LLM result cell
  const renderLlmCell = (promptData, model) => {
    const result = promptData.latestByModel?.[model];
    const llmConfig = LLM_CONFIG[model];
    const isCopied = copiedPrompt === `${promptData.id}-${model}`;

    const onClickLlm = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setCopiedPrompt(`${promptData.id}-${model}`);
      handleOpenLlm(model, promptData.prompt, promptData.languageCode, promptData.countryCode);
    };

    // If prompt has never been checked, don't show any link icons
    if (!promptData.lastCheckedAt) {
      return <span style={{ color: '#e5e7eb' }}>-</span>;
    }

    if (!result) {
      return (
        <button
          onClick={onClickLlm}
          style={{ background: 'none', border: 'none', color: isCopied ? '#10b981' : '#d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: '4px' }}
          title={model === 'PERPLEXITY' ? `Tester sur ${llmConfig.name}` : `Tester sur ${llmConfig.name} (copie le prompt)`}
        >
          {isCopied ? <Check size={14} /> : <ExternalLink size={14} />}
        </button>
      );
    }

    const isMentioned = result.isMentioned;
    const isCited = result.isCited;

    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
        {isMentioned ? (
          <InfoTooltip text={`Mentionné ${result.mentionCount}x`}>
            <Check size={16} style={{ color: '#10b981' }} />
          </InfoTooltip>
        ) : (
          <InfoTooltip text="Non mentionné">
            <X size={16} style={{ color: '#ef4444' }} />
          </InfoTooltip>
        )}
        {isCited && (
          <InfoTooltip text="Cité en source">
            <Link2 size={14} style={{ color: '#3b82f6' }} />
          </InfoTooltip>
        )}
        <InfoTooltip text={isCopied ? 'Copié !' : (model === 'PERPLEXITY' ? `Tester sur ${llmConfig.name}` : `Tester sur ${llmConfig.name} (copie le prompt)`)}>
          <button
            onClick={onClickLlm}
            style={{ background: 'none', border: 'none', color: isCopied ? '#10b981' : '#9ca3af', display: 'flex', alignItems: 'center', marginLeft: '4px', cursor: 'pointer', padding: '2px' }}
          >
            {isCopied ? <Check size={12} /> : <ExternalLink size={12} />}
          </button>
        </InfoTooltip>
      </div>
    );
  };

  // No project selected - show project list or create
  if (!selectedProject) {
    return (
      <div style={styles.container}>
        {/* En-tête */}
        <div style={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#111827',
                display: 'flex',
                alignItems: 'center',
                margin: '0 0 8px 0'
              }}>
                <Bot style={{ marginRight: '8px', height: '24px', width: '24px', color: '#3b82f6' }} />
                AI Visibility
              </h2>
              <p style={{ color: '#6b7280', margin: '0', fontSize: '14px' }}>
                Suivez votre visibilite dans les reponses LLM (ChatGPT, Claude, Gemini, Perplexity)
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                onClick={() => setShowNewProjectModal(true)}
                style={{ ...styles.button, ...styles.buttonSuccess }}
              >
                <Plus size={18} />
                Nouveau projet
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div style={{ ...styles.card, background: '#fef2f2', color: '#dc2626', marginBottom: '16px' }}>
            <AlertCircle size={18} style={{ marginRight: '8px' }} />
            {error}
          </div>
        )}

        {projects.length === 0 ? (
          <div style={{ ...styles.card, textAlign: 'center', padding: '48px' }}>
            <Bot size={48} style={{ color: '#d1d5db', marginBottom: '16px' }} />
            <h3 style={{ margin: '0 0 8px', color: '#374151' }}>Aucun projet</h3>
            <p style={{ color: '#6b7280', margin: '0 0 16px' }}>
              Creez votre premier projet pour commencer a suivre votre visibilite dans les LLM
            </p>
            <button
              onClick={() => setShowNewProjectModal(true)}
              style={{ ...styles.button, ...styles.buttonPrimary }}
            >
              <Plus size={18} />
              Créer un projet
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
            {projects.map(project => (
              <div
                key={project.id}
                style={{ ...styles.card, cursor: 'pointer', transition: 'transform 0.2s' }}
                onClick={() => setSelectedProject(project)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                      {project.name}
                    </h3>
                    <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>{project.domain}</p>
                    {project.clientName && (
                      <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                        Client: {project.clientName}
                      </p>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <div style={{
                      background: '#f3f4f6',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#374151',
                      whiteSpace: 'nowrap'
                    }}>
                      {project._count?.prompts || 0} prompts
                    </div>
                    <button
                      title="Modifier ce projet"
                      onClick={e => {
                        // Sans quoi le clic ouvrirait aussi le projet derrière la modale.
                        e.stopPropagation();
                        setProjetAEditer({
                          id: project.id,
                          name: project.name,
                          domain: project.domain,
                          clientId: project.clientId || ''
                        });
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9ca3af', display: 'flex' }}
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      title="Supprimer ce projet"
                      onClick={e => { e.stopPropagation(); deleteProject(project.id); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#ef4444', display: 'flex' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal: modification d'un projet */}
        {projetAEditer && ReactDOM.createPortal(
          <div style={styles.modalOverlay} onClick={() => setProjetAEditer(null)}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
              <h2 style={{ margin: '0 0 24px', fontSize: '20px', fontWeight: '600' }}>Modifier le projet</h2>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Nom du projet
                </label>
                <input
                  type="text"
                  value={projetAEditer.name}
                  onChange={e => setProjetAEditer({ ...projetAEditer, name: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Domaine
                </label>
                <input
                  type="text"
                  value={projetAEditer.domain}
                  onChange={e => setProjetAEditer({ ...projetAEditer, domain: e.target.value })}
                  placeholder="exemple.fr"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
                />
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '6px 0 0 0' }}>
                  C'est ce domaine qu'on cherche dans les réponses des modèles. Le changer ne
                  réécrit pas l'historique déjà relevé.
                </p>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Client (optionnel)
                </label>
                <select
                  value={projetAEditer.clientId}
                  onChange={e => setProjetAEditer({ ...projetAEditer, clientId: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box', backgroundColor: '#fff' }}
                >
                  <option value="">Aucun client</option>
                  {/* Triés : une liste de clients dans l'ordre de création oblige à la
                      parcourir en entier pour en trouver un. */}
                  {trierParNom(clients).map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button onClick={() => setProjetAEditer(null)} style={{ ...styles.button, background: '#f3f4f6', color: '#374151' }}>
                  Annuler
                </button>
                <button
                  onClick={enregistrerProjet}
                  disabled={!projetAEditer.name?.trim() || !projetAEditer.domain?.trim()}
                  style={{
                    ...styles.button, ...styles.buttonPrimary,
                    opacity: (!projetAEditer.name?.trim() || !projetAEditer.domain?.trim()) ? 0.5 : 1
                  }}
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Modal: New Project */}
        {showNewProjectModal && ReactDOM.createPortal(
          <div style={styles.modalOverlay} onClick={() => setShowNewProjectModal(false)}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
              <h2 style={{ margin: '0 0 24px', fontSize: '20px', fontWeight: '600' }}>Nouveau projet</h2>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Nom du projet
                </label>
                <input
                  type="text"
                  value={newProject.name}
                  onChange={e => setNewProject({ ...newProject, name: e.target.value })}
                  placeholder="Ex: Mon site"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Domaine/Marque a tracker
                </label>
                <input
                  type="text"
                  value={newProject.domain}
                  onChange={e => setNewProject({ ...newProject, domain: e.target.value })}
                  placeholder="Ex: monsite.fr"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Client (optionnel)
                </label>
                <select
                  value={newProject.clientId}
                  onChange={e => setNewProject({ ...newProject, clientId: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box', backgroundColor: '#fff' }}
                >
                  <option value="">Aucun client</option>
                  {/* Triés : une liste de clients dans l'ordre de création oblige à la
                      parcourir en entier pour en trouver un. */}
                  {trierParNom(clients).map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '6px 0 0 0' }}>Pour classer ce projet dans un dossier client</p>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowNewProjectModal(false)}
                  style={{ ...styles.button, ...styles.buttonSecondary }}
                >
                  Annuler
                </button>
                <button
                  onClick={createProject}
                  disabled={!newProject.name || !newProject.domain}
                  style={{ ...styles.button, ...styles.buttonSuccess, opacity: (!newProject.name || !newProject.domain) ? 0.5 : 1 }}
                >
                  Créer
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }

  // Project selected - show overview
  return (
    <div style={styles.container}>
      {/* En-tête */}
      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          {/* Le bandeau porte l'identité du projet ; la flèche est le seul retour vers la
              liste depuis que le sélecteur a disparu. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
            <button
              onClick={() => { setSelectedProject(null); setOverview(null); }}
              title="Tous les projets"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex', padding: '4px' }}
            >
              <ArrowLeft size={20} />
            </button>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0, display: 'flex', alignItems: 'center' }}>
                <Bot style={{ marginRight: '8px', height: '20px', width: '20px', color: '#3b82f6' }} />
                {selectedProject?.name}
              </h2>
              <p style={{ color: '#6b7280', margin: '2px 0 0', fontSize: '13px' }}>
                {selectedProject?.domain}{selectedProject?.clientName ? ` · ${selectedProject.clientName}` : ''}
              </p>
            </div>
          </div>

          {/* Les actions du projet prennent la place du bouton de création : la carte du
              sélecteur disparaît, et avec elle une rangée entière. */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              onClick={() => setShowAddPromptsModal(true)}
              style={{ ...styles.button, ...styles.buttonSuccess }}
            >
              <Plus size={18} />
              Ajouter prompts
            </button>
            <button
              onClick={releverTout}
              disabled={releveEnCours || !(overview?.prompts?.length)}
              style={{
                ...styles.button, ...styles.buttonSecondary,
                opacity: (releveEnCours || !(overview?.prompts?.length)) ? 0.5 : 1
              }}
              title="Interroger tous les prompts maintenant"
            >
              <RefreshCw size={18} style={{ animation: releveEnCours ? 'spin 1s linear infinite' : 'none' }} />
              Relever maintenant
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ ...styles.card, background: '#fef2f2', color: '#dc2626', marginBottom: '16px' }}>
          <AlertCircle size={18} style={{ marginRight: '8px' }} />
          {error}
          <button onClick={() => setError('')} style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={16} />
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', color: '#6b7280' }} />
          <p style={{ color: '#6b7280', marginTop: '16px' }}>Chargement...</p>
        </div>
      ) : overview && (
        <>
          {/* Stats Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
            <div style={styles.card}>
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Prompts</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>{overview.stats.totalPrompts}</div>
            </div>
            <div style={styles.card}>
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Taux mentions</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#10b981' }}>{overview.stats.mentionRate}%</div>
            </div>
            <div style={styles.card}>
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Taux citations</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#3b82f6' }}>{overview.stats.citationRate}%</div>
            </div>
            <div style={styles.card}>
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Cout total</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>${overview.stats.totalCost}</div>
            </div>
          </div>

          {/* Prompts Table */}
          <div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Prompt</th>
                    {/* Le nom complet, et donc `textTransform: 'none'` : la casse d'origine
                        distingue « ChatGPT » de « CHATGPT », et l'abréviation « GPT » forçait
                        à survoler pour savoir de quel modèle il s'agissait. */}
                    {Object.entries(LLM_CONFIG).map(([key, config]) => (
                      <th key={key} style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'none' }}>
                        <span style={{ color: config.color }}>{config.name}</span>
                      </th>
                    ))}
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...overview.prompts].sort((a, b) => {
                    // Unchecked prompts first
                    if (!a.lastCheckedAt && b.lastCheckedAt) return -1;
                    if (a.lastCheckedAt && !b.lastCheckedAt) return 1;
                    // Then by creation date (newest first)
                    return new Date(b.createdAt) - new Date(a.createdAt);
                  }).map(prompt => (
                    <tr key={prompt.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: '500', color: '#111827', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {prompt.prompt}
                        </div>
                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                          {prompt.lastCheckedAt ? new Date(prompt.lastCheckedAt).toLocaleDateString('fr-FR') : 'Jamais vérifié'}
                        </div>
                      </td>
                      {Object.keys(LLM_CONFIG).map(model => (
                        <td key={model} style={{ padding: '12px 16px', textAlign: 'center' }}>
                          {renderLlmCell(prompt, model)}
                        </td>
                      ))}
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <InfoTooltip text="historique" position="left">
                            <button
                              onClick={() => {
                                setSelectedPrompt(prompt);
                                loadPromptHistory(prompt.id);
                                setShowHistoryModal(true);
                              }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9ca3af' }}
                            >
                              <History size={16} />
                            </button>
                          </InfoTooltip>
                          <InfoTooltip text="vérifier maintenant" position="left">
                            <button
                              onClick={() => checkPrompt(prompt.id)}
                              disabled={checkingPrompt === prompt.id}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9ca3af', opacity: checkingPrompt === prompt.id ? 0.5 : 1 }}
                            >
                              <RefreshCw size={16} style={{ animation: checkingPrompt === prompt.id ? 'spin 1s linear infinite' : 'none' }} />
                            </button>
                          </InfoTooltip>
                          <InfoTooltip text="supprimer" position="left">
                            <button
                              onClick={() => deletePrompt(prompt.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#ef4444' }}
                            >
                              <Trash2 size={16} />
                            </button>
                          </InfoTooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {overview.prompts.length === 0 && (
              <div style={{ padding: '48px', textAlign: 'center' }}>
                <Search size={32} style={{ color: '#d1d5db', marginBottom: '8px' }} />
                <p style={{ color: '#6b7280', margin: '0 0 8px 0' }}>Aucun prompt ajoute</p>
                <button
                  onClick={() => setShowAddPromptsModal(true)}
                  style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Ajouter des prompts
                </button>
              </div>
            )}
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', fontSize: '13px', color: '#6b7280' }}>
            <InfoTooltip text="Le nom de ta marque apparaît dans le texte de la réponse du LLM" position="top">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'help' }}>
                <Check size={16} style={{ color: '#10b981' }} />
                <span>Mentionné</span>
              </div>
            </InfoTooltip>
            <InfoTooltip text="Le nom de ta marque n'apparaît pas dans le texte de la réponse" position="top">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'help' }}>
                <X size={16} style={{ color: '#ef4444' }} />
                <span>Non mentionné</span>
              </div>
            </InfoTooltip>
            <InfoTooltip text="Ton domaine apparaît dans les sources/références citées par le LLM" position="top">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'help' }}>
                <Link2 size={16} style={{ color: '#3b82f6' }} />
                <span>Cité en source</span>
              </div>
            </InfoTooltip>
          </div>
        </>
      )}

      {/* Modal: Add Prompts */}
      {showAddPromptsModal && ReactDOM.createPortal(
        <div style={styles.modalOverlay} onClick={() => setShowAddPromptsModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 24px', fontSize: '20px', fontWeight: '600' }}>
              Ajouter des prompts
            </h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Prompts (un par ligne)
              </label>
              <textarea
                value={newPrompts.prompts}
                onChange={e => setNewPrompts({ ...newPrompts, prompts: e.target.value })}
                placeholder="Ex: Quel est le meilleur outil SEA ?"
                rows={5}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                LLM à interroger
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {Object.entries(LLM_CONFIG).map(([key, config]) => (
                  <label
                    key={key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: `2px solid ${newPrompts.llmModels.includes(key) ? config.color : '#e5e7eb'}`,
                      cursor: 'pointer',
                      background: newPrompts.llmModels.includes(key) ? `${config.color}10` : '#fff'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={newPrompts.llmModels.includes(key)}
                      onChange={e => {
                        if (e.target.checked) {
                          setNewPrompts({ ...newPrompts, llmModels: [...newPrompts.llmModels, key] });
                        } else {
                          setNewPrompts({ ...newPrompts, llmModels: newPrompts.llmModels.filter(m => m !== key) });
                        }
                      }}
                      style={{ display: 'none' }}
                    />
                    <span style={{ color: config.color, fontWeight: '500' }}>{config.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Territoire
              </label>
              <select
                value={newPrompts.locationCode}
                onChange={e => {
                  const loc = locations.find(l => l.code === parseInt(e.target.value));
                  setNewPrompts({
                    ...newPrompts,
                    locationCode: parseInt(e.target.value),
                    locationName: loc?.name || 'France'
                  });
                }}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
              >
                {trierParNom(locations, l => nomDePays(l.countryIsoCode, l.name)).map(loc => (
                  <option key={loc.code} value={loc.code}>{nomDePays(loc.countryIsoCode, loc.name)}</option>
                ))}
              </select>
              {listesDegrade && (
                <div style={{ marginTop: '6px', fontSize: '12px', color: '#b45309', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                  <AlertCircle size={13} style={{ flexShrink: 0, marginTop: '1px' }} />
                  <span>{listesDegrade}</span>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Langue
                </label>
                <select
                  value={newPrompts.languageCode}
                  onChange={e => setNewPrompts({ ...newPrompts, languageCode: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
                >
                  {/* DataForSEO rend « French », « German »… au milieu d'une interface
                      française : on traduit, en gardant son libellé en cas de code inconnu. */}
                  {trierParNom(languages, l => nomDeLangue(l.code, l.name)).map(lang => (
                    <option key={lang.code} value={lang.code}>{nomDeLangue(lang.code, lang.name)}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Fréquence
                </label>
                <select
                  value={newPrompts.frequency}
                  onChange={e => setNewPrompts({ ...newPrompts, frequency: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
                >
                  <option value="DAILY">Quotidien</option>
                  <option value="WEEKLY">Hebdomadaire</option>
                  <option value="BIWEEKLY">Bi-mensuel</option>
                  <option value="MONTHLY">Mensuel</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAddPromptsModal(false)}
                style={{ ...styles.button, ...styles.buttonSecondary }}
              >
                Annuler
              </button>
              <button
                onClick={addPrompts}
                disabled={!newPrompts.prompts.trim() || newPrompts.llmModels.length === 0}
                style={{ ...styles.button, ...styles.buttonSuccess, opacity: (!newPrompts.prompts.trim() || newPrompts.llmModels.length === 0) ? 0.5 : 1 }}
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal: History */}
      {showHistoryModal && selectedPrompt && ReactDOM.createPortal(
        <div style={styles.modalOverlay} onClick={() => setShowHistoryModal(false)}>
          <div style={{ ...styles.modal, maxWidth: '800px' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: '600' }}>
              Historique
            </h2>
            <p style={{ margin: '0 0 24px', color: '#6b7280', fontSize: '14px' }}>
              {selectedPrompt.prompt}
            </p>

            {promptHistory.length > 0 ? (
              <>
                <div style={{ height: '250px', marginBottom: '24px' }}>
                  <ResponsiveContainer>
                    <LineChart data={(() => {
                      // Group history by date, with one entry per date containing all LLM results
                      const groups = {};
                      promptHistory.forEach(h => {
                        const dateKey = new Date(h.checkedAt).toLocaleDateString('fr-FR');
                        if (!groups[dateKey]) {
                          groups[dateKey] = { date: dateKey, checkedAt: h.checkedAt };
                        }
                        // Store the latest result for each LLM on that date
                        groups[dateKey][h.llmModel] = h.isMentioned ? 1 : 0;
                      });
                      return Object.values(groups).sort((a, b) => new Date(a.checkedAt) - new Date(b.checkedAt));
                    })()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis domain={[0, 1]} tickFormatter={v => v === 1 ? 'Oui' : 'Non'} />
                      <Tooltip formatter={(value, name) => [value === 1 ? 'Oui' : 'Non', name]} />
                      <Legend />
                      {Object.entries(LLM_CONFIG).map(([key, config]) => (
                        <Line
                          key={key}
                          type="monotone"
                          dataKey={key}
                          name={config.name}
                          stroke={config.color}
                          connectNulls
                          dot={{ r: 4 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '500' }}>Date</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: '500' }}>LLM</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: '500' }}>Mentionne</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: '500' }}>Cite</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promptHistory.map((h, i) => (
                        <tr key={i} style={{ borderTop: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px 12px' }}>{new Date(h.checkedAt).toLocaleDateString('fr-FR')}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            <span style={{ color: LLM_CONFIG[h.llmModel]?.color || '#6b7280' }}>
                              {LLM_CONFIG[h.llmModel]?.name || h.llmModel}
                            </span>
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            {h.isMentioned ? <Check size={16} style={{ color: '#10b981' }} /> : <X size={16} style={{ color: '#ef4444' }} />}
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            {h.isCited ? <Link2 size={16} style={{ color: '#3b82f6' }} /> : <span style={{ color: '#d1d5db' }}>-</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '32px', color: '#6b7280' }}>
                Aucun historique disponible
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => setShowHistoryModal(false)}
                style={{ ...styles.button, ...styles.buttonSecondary }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Toast notification */}
      {toastMessage && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#1f2937',
          color: '#fff',
          padding: '12px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          animation: 'slideUp 0.3s ease'
        }}>
          <Check size={18} style={{ color: '#10b981' }} />
          {toastMessage}
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default AiVisibilityTool;
